import { inject, Injectable } from '@angular/core';

import { CreateMutationOptions, CreateQueryOptions } from '@tanstack/angular-query-experimental';
import { AnnouncementPayloadType } from '@type/announcement.type';
import { Observable } from 'rxjs';

import { ApiQueryService } from '@services/helper/api-queries.service';
import { ApiService } from '@services/helper/api-wrapper.service';

/**
 * All calls for individual project announcements (FEC-1161) — CRUD for admins
 * plus the per-user read state behind the agent feed's unread dots.
 */
@Injectable({
  providedIn: 'root',
})
export class AnnouncementService {
  private readonly _api = inject(ApiService);
  private readonly _apiQuery = inject(ApiQueryService);

  /* ── Private API methods ──────────────────────────────────────── */

  /** GET /api/announcements */
  private getAnnouncements(
    params: { project_id: number; type?: string; page?: number; per_page?: number },
    signal?: AbortSignal,
  ): Observable<any> {
    return this._api.get('/announcements', params, {}, signal);
  }

  /** GET /api/announcements/pending */
  private getPendingAnnouncements(projectId: number, signal?: AbortSignal): Observable<any> {
    return this._api.get('/announcements/pending', { project_id: projectId }, {}, signal);
  }

  /**
   * GET /api/announcements — no project filter, for the header notification bell.
   *
   * `as_recipient` makes the API return what this user *receives* rather than
   * what they can administer. `unread_first` sorts unread rows to the top of
   * the page — without it an unread item could sit outside the fetched window
   * behind newer already-read ones, so the badge said "7 unread" while the
   * dropdown showed mostly read rows (FEC-1161 QA, Hina 12 Aug).
   */
  private getRecentAnnouncements(perPage: number, signal?: AbortSignal): Observable<any> {
    return this._api.get(
      '/announcements',
      { per_page: perPage, as_recipient: 1, unread_first: 1 },
      {},
      signal,
    );
  }

  /** GET /api/announcements/unread-count */
  private getUnreadAnnouncementCount(signal?: AbortSignal): Observable<any> {
    return this._api.get('/announcements/unread-count', {}, {}, signal);
  }

  /** POST /api/announcements */
  private createAnnouncement(payload: AnnouncementPayloadType): Observable<any> {
    return this._api.post('/announcements', payload);
  }

  /** PUT /api/announcements/:id */
  private updateAnnouncement(
    id: number,
    payload: Partial<AnnouncementPayloadType>,
  ): Observable<any> {
    return this._api.put(`/announcements/${id}`, payload);
  }

  /** DELETE /api/announcements/:id */
  private deleteAnnouncement(id: number): Observable<any> {
    return this._api.delete(`/announcements/${id}`);
  }

  /** POST /api/announcements/:id/read */
  private markAnnouncementRead(id: number): Observable<any> {
    return this._api.post(`/announcements/${id}/read`, {});
  }

  /**
   * POST /api/announcements/read-all
   *
   * `projectId` narrows it to one project (the project feed's button); omitting
   * it clears everything the user receives (the bell's button).
   */
  private markAllAnnouncementsRead(projectId?: number, asRecipient = false): Observable<any> {
    return this._api.post('/announcements/read-all', {
      ...(projectId ? { project_id: projectId } : {}),
      ...(asRecipient ? { as_recipient: 1 } : {}),
    });
  }

  /* ── Queries ──────────────────────────────────────────────────── */

  /**
   * Paginated announcements for a project. Accepts thunks so the caller's
   * signals drive refetching.
   */
  public queryGetAnnouncements(
    projectId: () => number,
    options: () => { type?: string; page?: number; per_page?: number } = () => ({}),
    config: Partial<CreateQueryOptions> = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getAnnouncements({ project_id: projectId(), ...options() }, signal),
      () => [this._apiQuery.ANNOUNCEMENTS, projectId(), JSON.stringify(options())],
      {},
      config,
    );
  }

  /** Announcements that should raise the pop-up for the current user. */
  public queryGetPendingAnnouncements(
    projectId: () => number,
    config: Partial<CreateQueryOptions> = {},
  ) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getPendingAnnouncements(projectId(), signal),
      () => [this._apiQuery.PENDING_ANNOUNCEMENTS, projectId()],
      {},
      config,
    );
  }

  /**
   * Most recent announcements across every project the user can open — the
   * header bell's dropdown. Not project-scoped, so it works on any page.
   */
  public queryGetRecentAnnouncements(perPage = 10, config: Partial<CreateQueryOptions> = {}) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getRecentAnnouncements(perPage, signal),
      () => [this._apiQuery.RECENT_ANNOUNCEMENTS, perPage],
      {},
      config,
    );
  }

  /** Unread total across every accessible project — the bell's badge. */
  public queryGetUnreadAnnouncementCount(config: Partial<CreateQueryOptions> = {}) {
    return this._apiQuery.executeCustomQuery(
      (signal) => this.getUnreadAnnouncementCount(signal),
      () => [this._apiQuery.ANNOUNCEMENTS_UNREAD_COUNT],
      {},
      config,
    );
  }

  /* ── Mutations ────────────────────────────────────────────────── */

  public createAnnouncementMutation(
    config: Partial<CreateMutationOptions<any, Error, AnnouncementPayloadType>> = {},
  ) {
    return this._apiQuery.executeCustomMutation(
      (payload: AnnouncementPayloadType) => this.createAnnouncement(payload),
      { ...config },
    );
  }

  public updateAnnouncementMutation(
    config: Partial<
      CreateMutationOptions<any, Error, { id: number; payload: Partial<AnnouncementPayloadType> }>
    > = {},
  ) {
    return this._apiQuery.executeCustomMutation(
      (params: { id: number; payload: Partial<AnnouncementPayloadType> }) =>
        this.updateAnnouncement(params.id, params.payload),
      { ...config },
    );
  }

  public deleteAnnouncementMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { id: number }) => this.deleteAnnouncement(params.id),
      { ...config },
    );
  }

  public markAnnouncementReadMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { id: number }) => this.markAnnouncementRead(params.id),
      { ...config },
    );
  }

  public markAllAnnouncementsReadMutation(config: Partial<CreateMutationOptions> = {}) {
    return this._apiQuery.executeCustomMutation(
      (params: { projectId?: number; asRecipient?: boolean }) =>
        this.markAllAnnouncementsRead(params.projectId, params.asRecipient),
      { ...config },
    );
  }

  /** Invalidates every announcement cache so lists, pop-ups and the bell re-sync. */
  public invalidateAnnouncements(): void {
    this._apiQuery.invalidateQuery([this._apiQuery.ANNOUNCEMENTS]);
    this._apiQuery.invalidateQuery([this._apiQuery.PENDING_ANNOUNCEMENTS]);
    this._apiQuery.invalidateQuery([this._apiQuery.RECENT_ANNOUNCEMENTS]);
    this._apiQuery.invalidateQuery([this._apiQuery.ANNOUNCEMENTS_UNREAD_COUNT]);
  }
}
